import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '@/components/Navigation';
import { SponsorBanner } from '@/components/SponsorBanner';
import PageSideRail from '@/components/PageSideRail';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import RichTextEditor from '@/components/RichTextEditor';
import RichText from '@/components/RichText';
import {
  Search, Building2, MapPin, Users, Globe, Leaf, Linkedin,
  ChevronLeft, ChevronRight, X, Filter, Handshake, PlusCircle, Clock, CheckCircle,
  Bookmark, BookmarkCheck, Star, Award
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = '';

function getFaviconUrl(website) {
  if (!website) return null;
  try {
    const domain = website.replace(/^https?:\/\//, '').split('/')[0];
    return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
  } catch { return null; }
}

function CompanyCard({ company, onClick, onBookmark }) {
  const favicon = getFaviconUrl(company.website);
  return (
    <Card className="group p-0 overflow-hidden border border-slate-100 hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
      onClick={() => onClick(company.company_id)} data-testid={`company-card-${company.company_id}`} title={company.is_partner ? 'View partner profile' : 'View company profile'}>
      <div className="h-1.5 bg-gradient-to-r from-brand-navy to-brand-sage" />
      <div className="p-5">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
            {company.logo_url ? (
              <img src={company.logo_url} alt={company.name} className="w-full h-full object-contain p-1" />
            ) : favicon ? (
              <img src={favicon} alt={company.name} className="w-8 h-8 object-contain" onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
            ) : null}
            {!company.logo_url && (
              <Building2 className="h-6 w-6 text-brand-navy/40" style={favicon ? { display: 'none' } : {}} />
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-0.5">
              <h3 className="font-bold text-sm text-slate-800 group-hover:text-brand-navy transition-colors truncate">{company.name}</h3>
              {company.is_partner && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-brand-sage text-white flex items-center gap-0.5">
                  <Handshake className="h-2.5 w-2.5" /> Partner
                </span>
              )}
              {company.is_sponsor && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-500 text-white flex items-center gap-0.5" data-testid={`sponsor-badge-${company.company_id}`}>
                  <Award className="h-2.5 w-2.5" /> Sponsor
                </span>
              )}
              {company.is_recommended && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold bg-amber-100 text-amber-700 flex items-center gap-0.5" data-testid={`recommended-badge-${company.company_id}`}>
                  <Star className="h-2.5 w-2.5" /> Recommended
                </span>
              )}
            </div>
            {company.tagline && <p className="text-[11px] text-brand-sage font-medium italic truncate">{company.tagline}</p>}
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onBookmark(company); }}
            className="flex-shrink-0 p-1.5 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid={`company-bookmark-btn-${company.company_id}`}
          >
            {company.is_bookmarked
              ? <BookmarkCheck className="h-4.5 w-4.5 text-brand-navy fill-brand-navy" />
              : <Bookmark className="h-4.5 w-4.5 text-slate-400 hover:text-brand-navy" />
            }
          </button>
        </div>

        <div className="text-sm text-slate-600 mb-3"><RichText html={company.description} className="card card-2" /></div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500 mb-3">
          {company.sector && <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{company.sector}</span>}
          {(company.hq_location || company.hq_country) && (
            <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{[company.hq_location, company.hq_country].filter(Boolean).join(', ')}</span>
          )}
          {company.company_size && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{company.company_size}</span>}
          {company.employee_count > 0 && <span className="flex items-center gap-1"><Users className="h-3 w-3" />{company.employee_count} staff on BriteHub</span>}
        </div>

        {(company.website || company.linkedin_url) && (
          <div className="flex items-center gap-3 text-xs text-slate-400 mb-3">
            {company.website && <span className="flex items-center gap-1 truncate"><Globe className="h-3 w-3" />{company.website.replace(/^https?:\/\//, '').split('/')[0]}</span>}
            {company.linkedin_url && <span className="flex items-center gap-1"><Linkedin className="h-3 w-3" />LinkedIn</span>}
          </div>
        )}

        {company.sustainability_focus?.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {company.sustainability_focus.slice(0, 3).map(f => (
              <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 font-medium">{f}</span>
            ))}
            {company.sustainability_focus.length > 3 && (
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-500">+{company.sustainability_focus.length - 3}</span>
            )}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function CompanyDirectoryPage({ user }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({});
  const [viewMode, setViewMode] = useState('all'); // 'all' | 'bookmarks' | 'recommended'

  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [sector, setSector] = useState(searchParams.get('sector') || '');
  const [size, setSize] = useState(searchParams.get('size') || '');
  const [focus, setFocus] = useState(searchParams.get('focus') || '');
  const [businessType, setBusinessType] = useState(searchParams.get('business_type') || '');
  const [showFilters, setShowFilters] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: '', description: '', tagline: '', sector: '', company_size: '',
    hq_location: '', hq_country: '', website: '', linkedin_url: '',
    contact_email: '', phone_number: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [mySubmissions, setMySubmissions] = useState([]);
  const [recommendations, setRecommendations] = useState([]);

  const token = sessionStorage.getItem('session_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const loadCompanies = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      if (viewMode === 'bookmarks') {
        const res = await fetch(`${BACKEND_URL}/api/companies/my-bookmarks`, { credentials: 'include', headers });
        if (res.ok) {
          const d = await res.json();
          setCompanies(d.companies || []);
          setTotal(d.total || 0);
          setPage(1);
          setTotalPages(1);
        }
      } else {
        const params = new URLSearchParams({ page: p, limit: 12 });
        if (search) params.set('search', search);
        if (sector) params.set('sector', sector);
        if (size) params.set('size', size);
        if (focus) params.set('focus', focus);
        if (businessType) params.set('business_type', businessType);

        const res = await fetch(`${BACKEND_URL}/api/companies?${params}`, { credentials: 'include', headers });
        if (res.ok) {
          const d = await res.json();
          setCompanies(d.companies || []);
          setTotal(d.total || 0);
          setPage(d.page || 1);
          setTotalPages(d.pages || 1);
          if (d.filters) setFilters(d.filters);
        }
      }
    } catch { }
    setLoading(false);
  }, [search, sector, size, focus, businessType, viewMode]);

  useEffect(() => { loadCompanies(1); }, [search, sector, size, focus, businessType, viewMode]);

  const loadRecommendations = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/recommendations/my`, { credentials: 'include', headers });
      if (res.ok) {
        const d = await res.json();
        setRecommendations(d.recommendations || []);
      }
    } catch {}
  };

  useEffect(() => { loadRecommendations(); }, []);

  const handleBookmark = async (company) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/${company.company_id}/bookmark`, {
        method: 'POST', credentials: 'include', headers,
      });
      if (res.ok) {
        const data = await res.json();
        setCompanies(prev => prev.map(c =>
          c.company_id === company.company_id ? { ...c, is_bookmarked: data.bookmarked } : c
        ));
        toast.success(data.bookmarked ? 'Company bookmarked!' : 'Bookmark removed');
        if (viewMode === 'bookmarks' && !data.bookmarked) {
          setCompanies(prev => prev.filter(c => c.company_id !== company.company_id));
        }
      }
    } catch { toast.error('Failed to bookmark'); }
  };

  const handleSearch = (val) => {
    setSearch(val);
    const p = new URLSearchParams(searchParams);
    if (val) p.set('search', val); else p.delete('search');
    setSearchParams(p);
  };

  const setFilter = (key, val, setter) => {
    setter(val);
    const p = new URLSearchParams(searchParams);
    if (val) p.set(key, val); else p.delete(key);
    setSearchParams(p);
  };

  const clearAllFilters = () => {
    setSearch(''); setSector(''); setSize(''); setFocus(''); setBusinessType('');
    setSearchParams({});
  };

  const loadMySubmissions = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies/my-submissions`, { credentials: 'include', headers });
      if (res.ok) setMySubmissions(await res.json());
    } catch {}
  };

  useEffect(() => { loadMySubmissions(); }, []);

  const handleRegisterCompany = async () => {
    if (!registerForm.name.trim()) { toast.error('Company name is required'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/companies`, {
        method: 'POST', credentials: 'include', headers,
        body: JSON.stringify(registerForm),
      });
      if (!res.ok) { const err = await res.json(); throw new Error(err.detail || 'Failed to submit'); }
      toast.success('Company submitted for review! An admin will review your submission shortly.');
      setShowRegister(false);
      setRegisterForm({ name: '', description: '', tagline: '', sector: '', company_size: '', hq_location: '', hq_country: '', website: '', linkedin_url: '', contact_email: '', phone_number: '' });
      loadMySubmissions();
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  const activeFilterCount = [sector, size, focus, businessType].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-slate-50">
      <Navigation user={user} />

      <main className="pt-24 pb-24 lg:pb-12 px-4 sm:px-6">
        <div className="container mx-auto max-w-7xl">
          <SponsorBanner placement="companies" type="top" className="mb-6 flex justify-center" />
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0">
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold font-heading gradient-text" data-testid="company-directory-title">Company Directory</h1>
              <p className="text-slate-500 mt-1">Discover organisations driving sustainability in logistics</p>
            </div>
            <Button onClick={() => setShowRegister(true)} className="bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="register-company-btn">
              <PlusCircle className="h-4 w-4 mr-2" /> Register Your Company
            </Button>
          </div>

          {/* My Pending Submissions */}
          {mySubmissions.filter(s => s.status === 'pending').length > 0 && (
            <Card className="p-4 mb-6 border-l-4 border-l-amber-400 bg-amber-50/50" data-testid="my-pending-submissions">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-4 w-4 text-amber-600" />
                <h3 className="text-sm font-bold text-amber-800">Your Pending Submissions</h3>
              </div>
              <div className="space-y-2">
                {mySubmissions.filter(s => s.status === 'pending').map(s => (
                  <div key={s.company_id} className="flex items-center justify-between text-sm" data-testid={`my-submission-${s.company_id}`}>
                    <span className="text-slate-700 font-medium">{s.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Awaiting Review</span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <div className="flex flex-col sm:flex-row gap-3 mb-6">
            <div className="flex gap-2 flex-wrap">
              <Button variant={viewMode === 'all' ? 'default' : 'outline'} size="sm"
                onClick={() => setViewMode('all')}
                className={viewMode === 'all' ? 'bg-brand-navy text-white' : ''} data-testid="company-view-all">
                All Companies
              </Button>
              <Button variant={viewMode === 'bookmarks' ? 'default' : 'outline'} size="sm"
                onClick={() => setViewMode(viewMode === 'bookmarks' ? 'all' : 'bookmarks')}
                className={viewMode === 'bookmarks' ? 'bg-brand-navy text-white' : ''} data-testid="company-view-bookmarks">
                <BookmarkCheck className="h-3.5 w-3.5 mr-1" /> My Bookmarks
              </Button>
              {recommendations.length > 0 && (
                <Button variant={viewMode === 'recommended' ? 'default' : 'outline'} size="sm"
                  onClick={() => setViewMode(viewMode === 'recommended' ? 'all' : 'recommended')}
                  className={viewMode === 'recommended' ? 'bg-amber-500 text-white' : 'border-amber-200 text-amber-700'} data-testid="company-view-recommended">
                  <Star className="h-3.5 w-3.5 mr-1" /> Recommended ({recommendations.length})
                </Button>
              )}
            </div>
            {viewMode === 'all' && (
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Search companies by name, sector..."
                  value={search} onChange={e => handleSearch(e.target.value)}
                  className="pl-10" data-testid="company-search-input" />
              </div>
            )}
            {viewMode === 'all' && (
              <Button variant={showFilters ? 'default' : 'outline'} onClick={() => setShowFilters(!showFilters)}
                data-testid="toggle-company-filters" className={showFilters ? 'bg-brand-navy text-white' : ''}>
                <Filter className="h-4 w-4 mr-1.5" />
                Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
              </Button>
            )}
          </div>

          {showFilters && viewMode === 'all' && (
            <Card className="p-5 mb-6 animate-in slide-in-from-top-2" data-testid="company-filter-panel">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Sector</label>
                  <select value={businessType} onChange={e => setFilter('business_type', e.target.value, setBusinessType)}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="company-filter-business-type">
                    <option value="">All Sectors</option>
                    {(filters.business_types || []).map(bt => <option key={bt} value={bt}>{bt}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 uppercase mb-1.5 block">Sustainability Focus</label>
                  <select value={focus} onChange={e => setFilter('focus', e.target.value, setFocus)}
                    className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" data-testid="company-filter-focus">
                    <option value="">All Focus Areas</option>
                    {(filters.sustainability_focus || []).map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <Button variant="ghost" size="sm" className="mt-3 text-xs text-slate-500" onClick={clearAllFilters}>
                  <X className="h-3 w-3 mr-1" /> Clear all filters
                </Button>
              )}
            </Card>
          )}

          <p className="text-sm text-slate-500 mb-4" data-testid="company-results-count">
            {viewMode === 'bookmarks' ? `${total} bookmarked compan${total !== 1 ? 'ies' : 'y'}` :
             viewMode === 'recommended' ? `${recommendations.length} recommended compan${recommendations.length !== 1 ? 'ies' : 'y'}` :
             `${total} compan${total !== 1 ? 'ies' : 'y'} found`}
          </p>

          {/* Recommendations view */}
          {viewMode === 'recommended' && (
            <div className="space-y-4" data-testid="recommended-companies-list">
              {recommendations.map(rec => (
                <Card key={rec.recommendation_id} className="p-5 hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => navigate(`/companies/${rec.company_id}`)} data-testid={`recommendation-${rec.recommendation_id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 flex items-center justify-center flex-shrink-0 overflow-hidden">
                      {rec.company?.logo_url ? (
                        <img src={rec.company.logo_url} alt="" className="w-full h-full object-contain p-1 rounded-xl" />
                      ) : (
                        <Building2 className="h-6 w-6 text-amber-500" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-bold text-slate-800 mb-0.5">{rec.company?.name || rec.company_name}</h3>
                      {rec.company?.sector && <p className="text-xs text-slate-500 mb-1">{rec.company.sector}</p>}
                      {rec.note && (
                        <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 mt-2 italic">"{rec.note}"</p>
                      )}
                      <p className="text-xs text-slate-400 mt-2">
                        Recommended by {rec.admin_name} &middot; {new Date(rec.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </Card>
              ))}
              {recommendations.length === 0 && (
                <Card className="p-12 text-center">
                  <Star className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No recommendations yet</p>
                </Card>
              )}
            </div>
          )}

          {/* All / Bookmarks view */}
          {viewMode !== 'recommended' && (<>
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full" />
            </div>
          ) : companies.length === 0 ? (
            <Card className="p-12 text-center">
              {viewMode === 'bookmarks' ? (
                <>
                  <Bookmark className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No bookmarked companies yet</p>
                  <p className="text-sm text-slate-400 mt-1">Bookmark companies to save them here for quick access</p>
                </>
              ) : (
                <>
                  <Building2 className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500">No companies match your criteria</p>
                  {activeFilterCount > 0 && (
                    <Button variant="outline" size="sm" className="mt-3" onClick={clearAllFilters}>Clear Filters</Button>
                  )}
                </>
              )}
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="company-grid">
              {companies.map(c => (
                <CompanyCard
                  key={c.company_id}
                  company={c}
                  onClick={() => {
                    // Partners get the richer partner detail page; companies
                    // fall back to the standard company profile. Pass `from`
                    // in router state so the partner page's "Back" button
                    // returns to wherever we originated (Company directory
                    // here, not the default /partners list).
                    if (c.is_partner && c.partner_sponsor_id) {
                      navigate(`/partners/${c.partner_sponsor_id}`, { state: { from: '/companies' } });
                    } else {
                      navigate(`/companies/${c.company_id}`);
                    }
                  }}
                  onBookmark={handleBookmark}
                />
              ))}
            </div>
          )}

          {viewMode !== 'bookmarks' && totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-8">
              <Button variant="outline" size="sm" disabled={page <= 1}
                onClick={() => loadCompanies(page - 1)} data-testid="company-prev-page">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-slate-500">Page {page} of {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages}
                onClick={() => loadCompanies(page + 1)} data-testid="company-next-page">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          </>)}

          {/* Register Company Dialog */}
          <Dialog open={showRegister} onOpenChange={setShowRegister}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="register-company-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-brand-navy" /> Register Your Company
                </DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-500 -mt-2 mb-2">Submit your company details for review. Once approved, it will appear in the directory.</p>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input value={registerForm.name} onChange={e => setRegisterForm({...registerForm, name: e.target.value})}
                      placeholder="e.g. Shape Tomorrow Ltd" data-testid="register-company-name" />
                  </div>
                  <div>
                    <Label>Tagline</Label>
                    <Input value={registerForm.tagline} onChange={e => setRegisterForm({...registerForm, tagline: e.target.value})}
                      placeholder="e.g. Shaping sustainable futures" />
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <RichTextEditor value={registerForm.description} onChange={v => setRegisterForm({...registerForm, description: v})}
                    placeholder="Describe what your company does..." minHeight="6rem" testId="register-company-desc" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Sector</Label>
                    <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                      value={registerForm.sector} onChange={e => setRegisterForm({...registerForm, sector: e.target.value})} data-testid="register-company-sector">
                      <option value="">Select sector</option>
                      {["Supply Chain & Logistics", "Technology & SaaS", "Energy & Utilities", "Manufacturing", "Retail & Consumer", "Finance & Insurance", "Consulting & Advisory", "Transport & Freight", "Waste & Recycling", "Construction & Infrastructure", "Agriculture & Food", "Healthcare & Pharma", "Government & Public Sector", "Education & Research", "Other"].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label>Company Size</Label>
                    <select className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm"
                      value={registerForm.company_size} onChange={e => setRegisterForm({...registerForm, company_size: e.target.value})} data-testid="register-company-size">
                      <option value="">Select size</option>
                      {["1-10", "11-50", "51-200", "201-500", "501-1000", "1001-5000", "5000+"].map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>HQ Location</Label>
                    <Input value={registerForm.hq_location} onChange={e => setRegisterForm({...registerForm, hq_location: e.target.value})}
                      placeholder="e.g. London" />
                  </div>
                  <div>
                    <Label>Country</Label>
                    <Input value={registerForm.hq_country} onChange={e => setRegisterForm({...registerForm, hq_country: e.target.value})}
                      placeholder="e.g. United Kingdom" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Website</Label>
                    <Input value={registerForm.website} onChange={e => setRegisterForm({...registerForm, website: e.target.value})}
                      placeholder="https://example.com" />
                  </div>
                  <div>
                    <Label>LinkedIn URL</Label>
                    <Input value={registerForm.linkedin_url} onChange={e => setRegisterForm({...registerForm, linkedin_url: e.target.value})}
                      placeholder="https://linkedin.com/company/..." />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Email</Label>
                    <Input type="email" value={registerForm.contact_email} onChange={e => setRegisterForm({...registerForm, contact_email: e.target.value})}
                      placeholder="info@example.com" />
                  </div>
                  <div>
                    <Label>Phone Number</Label>
                    <Input value={registerForm.phone_number} onChange={e => setRegisterForm({...registerForm, phone_number: e.target.value})}
                      placeholder="+44 20 1234 5678" />
                  </div>
                </div>
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700">
                  Your submission will be reviewed by an admin before appearing in the directory. You'll see its status in "Your Pending Submissions" above.
                </div>
                <Button onClick={handleRegisterCompany} disabled={submitting}
                  className="w-full bg-gradient-to-r from-brand-navy to-brand-sage text-white" data-testid="submit-company-btn">
                  {submitting ? 'Submitting...' : 'Submit for Review'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
            </div>
            <PageSideRail placement="companies" />
          </div>
        </div>
      </main>
    </div>
  );
}
